import { fetchAuthSession } from '@aws-amplify/core';

import api from '../axios';

/* =========================================================
   CORE ENUM VALUES
   ========================================================= */

export const INCIDENT_REPORT_TYPES = [
  'MEDICAL',
  'SECURITY_INCIDENT',
  'SUSPICIOUS_PERSON',
  'SUSPICIOUS_VEHICLE',
  'FIRE_SMOKE',
  'SAFETY_HAZARD',
  'ACCESS_CONTROL',
  'PROPERTY_DAMAGE',
  'THEFT_LOSS',
  'CLIENT_DISSATISFACTION',
  'CLIENT_PRAISE',
  'OTHER',
] as const;

export const INCIDENT_REPORT_STATUSES = ['DRAFT', 'SUBMITTED'] as const;

export const INCIDENT_REPORT_SEVERITIES = [
  'LOW',
  'MEDIUM',
  'HIGH',
  'CRITICAL',
] as const;

export const INCIDENT_ATTACHMENT_TYPES = [
  'PHOTO',
  'DOCUMENT',
  'OTHER',
] as const;

/* =========================================================
   PERSONS INVOLVED
   ========================================================= */

export const INCIDENT_PERSON_ROLES = [
  'PRINCIPAL',
  'FAMILY_MEMBER',
  'GUEST',
  'EMPLOYEE',
  'CONTRACTOR',
  'PROTECTOR',
  'WITNESS',
  'SUBJECT',
  'VICTIM',
  'LAW_ENFORCEMENT',
  'EMS',
  'FIRE_DEPARTMENT',
  'OTHER',
  'UNKNOWN',
] as const;

/* =========================================================
   COMMON OBSERVATIONAL VALUES
   ========================================================= */

export const INCIDENT_DIRECTIONS = [
  'NORTH',
  'NORTHEAST',
  'EAST',
  'SOUTHEAST',
  'SOUTH',
  'SOUTHWEST',
  'WEST',
  'NORTHWEST',
  'STATIONARY',
  'UNKNOWN',
] as const;

export const INCIDENT_US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
  'OTHER',
  'UNKNOWN',
] as const;

/* =========================================================
   SUSPICIOUS PERSON VALUES
   ========================================================= */

export const INCIDENT_PERSON_SEX_PRESENTATIONS = [
  'MALE',
  'FEMALE',
  'OTHER',
  'UNKNOWN',
] as const;

export const INCIDENT_PERSON_AGE_RANGES = [
  'CHILD',
  'TEEN',
  'AGE_18_24',
  'AGE_25_34',
  'AGE_35_44',
  'AGE_45_54',
  'AGE_55_64',
  'AGE_65_PLUS',
  'UNKNOWN',
] as const;

export const INCIDENT_PERSON_BUILDS = [
  'VERY_SLIM',
  'SLIM',
  'AVERAGE',
  'ATHLETIC',
  'HEAVY',
  'VERY_HEAVY',
  'UNKNOWN',
] as const;

export const INCIDENT_HAIR_COLORS = [
  'BLACK',
  'BROWN',
  'BLONDE',
  'RED',
  'GRAY',
  'WHITE',
  'DYED_OTHER',
  'BALD',
  'UNKNOWN',
] as const;

export const INCIDENT_HAIR_LENGTHS = [
  'BALD',
  'VERY_SHORT',
  'SHORT',
  'MEDIUM',
  'LONG',
  'UNKNOWN',
] as const;

export const INCIDENT_FACIAL_HAIR_TYPES = [
  'NONE',
  'STUBBLE',
  'MUSTACHE',
  'GOATEE',
  'BEARD',
  'OTHER',
  'UNKNOWN',
] as const;

/* =========================================================
   VEHICLE VALUES
   ========================================================= */

export const INCIDENT_VEHICLE_BODY_TYPES = [
  'SEDAN',
  'COUPE',
  'HATCHBACK',
  'SUV',
  'CROSSOVER',
  'PICKUP',
  'VAN',
  'MINIVAN',
  'WAGON',
  'CONVERTIBLE',
  'MOTORCYCLE',
  'COMMERCIAL_TRUCK',
  'BOX_TRUCK',
  'BUS',
  'RV',
  'OTHER',
  'UNKNOWN',
] as const;

export const INCIDENT_VEHICLE_COLORS = [
  'BLACK',
  'WHITE',
  'GRAY',
  'SILVER',
  'RED',
  'BLUE',
  'GREEN',
  'BROWN',
  'TAN',
  'BEIGE',
  'YELLOW',
  'ORANGE',
  'PURPLE',
  'GOLD',
  'MULTICOLOR',
  'OTHER',
  'UNKNOWN',
] as const;

/* =========================================================
   MEDICAL VALUES
   ========================================================= */

export const INCIDENT_MEDICAL_NATURES = [
  'FALL',
  'CUT_LACERATION',
  'BLUNT_TRAUMA',
  'HEAD_INJURY',
  'BURN',
  'ALLERGIC_REACTION',
  'BREATHING_DIFFICULTY',
  'CHEST_PAIN',
  'LOSS_OF_CONSCIOUSNESS',
  'SEIZURE',
  'DIABETIC_EMERGENCY',
  'HEAT_ILLNESS',
  'INTOXICATION',
  'OVERDOSE',
  'CARDIAC_ARREST',
  'ILLNESS',
  'PAIN',
  'OTHER',
  'UNKNOWN',
] as const;

export const INCIDENT_MEDICAL_CARE_ACTIONS = [
  'ASSESSMENT',
  'BASIC_FIRST_AID',
  'BLEEDING_CONTROL',
  'CPR',
  'AED',
  'OXYGEN',
  'EMS_TRANSFER',
  'MONITORED_ONLY',
  'OTHER',
  'NONE',
] as const;

/* =========================================================
   SECURITY VALUES
   ========================================================= */

export const INCIDENT_SECURITY_SUBTYPES = [
  'TRESPASS',
  'UNAUTHORIZED_ENTRY',
  'THREAT',
  'ASSAULT',
  'BATTERY',
  'FIGHT',
  'HARASSMENT',
  'STALKING',
  'VANDALISM',
  'DISTURBANCE',
  'WEAPON',
  'DOMESTIC_INCIDENT',
  'MISSING_PERSON',
  'POLICE_ACTIVITY',
  'SECURITY_BREACH',
  'POLICY_VIOLATION',
  'OTHER',
] as const;

/* =========================================================
   FIRE / SMOKE VALUES
   ========================================================= */

export const INCIDENT_FIRE_SOURCES = [
  'ELECTRICAL',
  'COOKING',
  'CANDLE',
  'FIREPLACE',
  'SMOKING',
  'VEHICLE',
  'BATTERY',
  'APPLIANCE',
  'CONSTRUCTION',
  'WILDLAND',
  'OTHER',
  'UNKNOWN',
] as const;

/* =========================================================
   SAFETY HAZARD VALUES
   ========================================================= */

export const INCIDENT_HAZARD_TYPES = [
  'SLIP_TRIP_FALL',
  'BLOCKED_EXIT',
  'ELECTRICAL',
  'FIRE',
  'CHEMICAL',
  'STRUCTURAL',
  'SHARP_OBJECT',
  'BROKEN_GLASS',
  'WATER_LEAK',
  'LIGHTING',
  'VEHICLE_TRAFFIC',
  'POOL_WATER',
  'ANIMAL',
  'WEATHER',
  'EQUIPMENT',
  'OTHER',
] as const;

/* =========================================================
   ACCESS CONTROL VALUES
   ========================================================= */

export const INCIDENT_ACCESS_METHODS = [
  'KEY',
  'KEYCARD',
  'BADGE',
  'BIOMETRIC',
  'PIN_CODE',
  'REMOTE_RELEASE',
  'INTERCOM',
  'TAILGATING',
  'FORCED_ENTRY',
  'UNLOCKED_DOOR',
  'UNLOCKED_GATE',
  'UNKNOWN',
  'OTHER',
] as const;

export const INCIDENT_ACCESS_POINT_TYPES = [
  'VEHICLE_GATE',
  'PEDESTRIAN_GATE',
  'FRONT_DOOR',
  'REAR_DOOR',
  'SERVICE_ENTRANCE',
  'GARAGE',
  'ELEVATOR',
  'STAIRWELL',
  'OFFICE',
  'RESIDENTIAL_AREA',
  'PERIMETER',
  'OTHER',
] as const;

/* =========================================================
   PROPERTY DAMAGE VALUES
   ========================================================= */

export const INCIDENT_PROPERTY_CATEGORIES = [
  'VEHICLE',
  'BUILDING',
  'DOOR_GATE',
  'WINDOW_GLASS',
  'FURNITURE',
  'ELECTRONICS',
  'SECURITY_EQUIPMENT',
  'LANDSCAPING',
  'ART_VALUABLE',
  'PERSONAL_PROPERTY',
  'OTHER',
] as const;

export const INCIDENT_DAMAGE_CAUSES = [
  'ACCIDENTAL',
  'INTENTIONAL',
  'WEATHER',
  'MECHANICAL_FAILURE',
  'VEHICLE',
  'ANIMAL',
  'UNKNOWN',
  'OTHER',
] as const;

/* =========================================================
   THEFT / LOSS VALUES
   ========================================================= */

export const INCIDENT_LOSS_TYPES = [
  'THEFT',
  'MISSING',
  'LOST',
  'UNKNOWN',
] as const;

export const INCIDENT_PROPERTY_LOSS_CATEGORIES = [
  'CASH',
  'JEWELRY',
  'WATCH',
  'ELECTRONICS',
  'VEHICLE',
  'KEY',
  'ACCESS_CREDENTIAL',
  'DOCUMENT',
  'ART',
  'CLOTHING',
  'PACKAGE',
  'OTHER',
] as const;

/* =========================================================
   CLIENT FEEDBACK VALUES
   ========================================================= */

export const INCIDENT_CLIENT_SOURCES = [
  'PRINCIPAL',
  'FAMILY_MEMBER',
  'ESTATE_MANAGER',
  'EXECUTIVE_ASSISTANT',
  'HOUSEHOLD_STAFF',
  'GUEST',
  'CLIENT_REPRESENTATIVE',
  'OTHER',
] as const;

export const INCIDENT_COMPLAINT_CATEGORIES = [
  'SERVICE',
  'PROFESSIONALISM',
  'COMMUNICATION',
  'APPEARANCE',
  'TIMELINESS',
  'SECURITY_PROCEDURE',
  'PRIVACY',
  'DRIVING',
  'CONDUCT',
  'ATTENTIVENESS',
  'STAFFING',
  'OTHER',
] as const;

export const INCIDENT_PRAISE_CATEGORIES = [
  'SERVICE',
  'PROFESSIONALISM',
  'COMMUNICATION',
  'ATTENTIVENESS',
  'INITIATIVE',
  'SECURITY_RESPONSE',
  'DISCRETION',
  'DRIVING',
  'PROBLEM_SOLVING',
  'APPEARANCE',
  'OTHER',
] as const;

/* =========================================================
   TYPES DERIVED FROM ENUM VALUES
   ========================================================= */

export type IncidentReportType = (typeof INCIDENT_REPORT_TYPES)[number];

export type IncidentReportStatus = (typeof INCIDENT_REPORT_STATUSES)[number];

export type IncidentReportSeverity =
  (typeof INCIDENT_REPORT_SEVERITIES)[number];

export type IncidentAttachmentType = (typeof INCIDENT_ATTACHMENT_TYPES)[number];

export type IncidentPersonRole = (typeof INCIDENT_PERSON_ROLES)[number];

export type IncidentDirection = (typeof INCIDENT_DIRECTIONS)[number];

export type IncidentUSState = (typeof INCIDENT_US_STATES)[number];

export type IncidentPersonSexPresentation =
  (typeof INCIDENT_PERSON_SEX_PRESENTATIONS)[number];

export type IncidentPersonAgeRange =
  (typeof INCIDENT_PERSON_AGE_RANGES)[number];

export type IncidentPersonBuild = (typeof INCIDENT_PERSON_BUILDS)[number];

export type IncidentHairColor = (typeof INCIDENT_HAIR_COLORS)[number];

export type IncidentHairLength = (typeof INCIDENT_HAIR_LENGTHS)[number];

export type IncidentFacialHairType =
  (typeof INCIDENT_FACIAL_HAIR_TYPES)[number];

export type IncidentVehicleBodyType =
  (typeof INCIDENT_VEHICLE_BODY_TYPES)[number];

export type IncidentVehicleColor = (typeof INCIDENT_VEHICLE_COLORS)[number];

export type IncidentMedicalNature = (typeof INCIDENT_MEDICAL_NATURES)[number];

export type IncidentMedicalCareAction =
  (typeof INCIDENT_MEDICAL_CARE_ACTIONS)[number];

export type IncidentSecuritySubtype =
  (typeof INCIDENT_SECURITY_SUBTYPES)[number];

export type IncidentFireSource = (typeof INCIDENT_FIRE_SOURCES)[number];

export type IncidentHazardType = (typeof INCIDENT_HAZARD_TYPES)[number];

export type IncidentAccessMethod = (typeof INCIDENT_ACCESS_METHODS)[number];

export type IncidentAccessPointType =
  (typeof INCIDENT_ACCESS_POINT_TYPES)[number];

export type IncidentPropertyCategory =
  (typeof INCIDENT_PROPERTY_CATEGORIES)[number];

export type IncidentDamageCause = (typeof INCIDENT_DAMAGE_CAUSES)[number];

export type IncidentLossType = (typeof INCIDENT_LOSS_TYPES)[number];

export type IncidentPropertyLossCategory =
  (typeof INCIDENT_PROPERTY_LOSS_CATEGORIES)[number];

export type IncidentClientSource = (typeof INCIDENT_CLIENT_SOURCES)[number];

export type IncidentComplaintCategory =
  (typeof INCIDENT_COMPLAINT_CATEGORIES)[number];

export type IncidentPraiseCategory =
  (typeof INCIDENT_PRAISE_CATEGORIES)[number];

/* =========================================================
   PERSON INVOLVED
   ========================================================= */

export type IncidentPersonInvolved = {
  _id?: string;

  name?: string;

  role?: IncidentPersonRole;

  roleOther?: string;

  notes?: string;
};

/* =========================================================
   ATTACHMENT
   ========================================================= */

export type IncidentAttachment = {
  _id?: string;

  type: IncidentAttachmentType;

  storageKey: string;

  fileName?: string;

  mimeType?: string;

  size?: number;

  sha256?: string;

  uploadedByUserId?: string;

  uploadedAt?: string;

  /**
   * Later populated by detail endpoints through
   * temporary signed S3 URLs.
   */
  url?: string;
};

/* =========================================================
   MEDICAL DETAILS
   ========================================================= */

export type IncidentMedicalDetails = {
  /*
   * Multi-select standardized values.
   */
  incidentNatures?: IncidentMedicalNature[];

  /*
   * Only populated when incidentNatures contains OTHER.
   */
  incidentNatureOther?: string;

  /*
   * Multi-select standardized values.
   */
  careActions?: IncidentMedicalCareAction[];

  /*
   * Only populated when careActions contains OTHER.
   */
  careActionOther?: string;

  /*
   * Free-text detail remains useful for clinical context
   * and later NLP training.
   */
  careProvidedNarrative?: string;

  emsCalled?: boolean;

  emsAgency?: string;

  transported?: boolean;

  destination?: string;

  refusedCare?: boolean;

  additionalNotes?: string;
};

/* =========================================================
   SECURITY INCIDENT DETAILS
   ========================================================= */

export type IncidentSecurityDetails = {
  incidentSubtype?: IncidentSecuritySubtype;

  incidentSubtypeOther?: string;

  lawEnforcementContacted?: boolean;

  agency?: string;

  officerName?: string;

  caseNumber?: string;

  additionalNotes?: string;
};

/* =========================================================
   SUSPICIOUS PERSON DETAILS
   ========================================================= */

export type IncidentSuspiciousPersonDetails = {
  sexPresentation?: IncidentPersonSexPresentation;

  approximateAgeRange?: IncidentPersonAgeRange;

  /**
   * CANONICAL STORAGE UNIT = CENTIMETERS.
   *
   * Do NOT send feet/inches to the API.
   *
   * The web/native UI converts:
   *
   * feet + inches -> centimeters
   *
   * before creating the payload.
   *
   * Example:
   *
   * 5'11" -> 180
   */
  estimatedHeightCm?: number;

  build?: IncidentPersonBuild;

  clothing?: string;

  hairColor?: IncidentHairColor;

  hairLength?: IncidentHairLength;

  facialHair?: IncidentFacialHairType;

  identifyingFeatures?: string;

  behavior?: string;

  directionOfTravel?: IncidentDirection;

  associatedVehicle?: string;
};

/* =========================================================
   SUSPICIOUS VEHICLE DETAILS
   ========================================================= */

export type IncidentSuspiciousVehicleDetails = {
  licensePlate?: string;

  state?: IncidentUSState;

  make?: string;

  model?: string;

  color?: IncidentVehicleColor;

  bodyType?: IncidentVehicleBodyType;

  occupantCount?: number;

  distinguishingMarks?: string;

  directionOfTravel?: IncidentDirection;
};

/* =========================================================
   FIRE / SMOKE DETAILS
   ========================================================= */

export type IncidentFireSmokeDetails = {
  sourceLocation?: string;

  suspectedSource?: IncidentFireSource;

  suspectedSourceOther?: string;

  alarmActivated?: boolean;

  extinguisherUsed?: boolean;

  evacuationOccurred?: boolean;

  fireDepartmentCalled?: boolean;

  fireDepartmentAgency?: string;

  additionalNotes?: string;
};

/* =========================================================
   SAFETY HAZARD DETAILS
   ========================================================= */

export type IncidentSafetyHazardDetails = {
  hazardTypes?: IncidentHazardType[];

  hazardTypeOther?: string;

  areaSecured?: boolean;

  correctiveAction?: string;

  additionalNotes?: string;
};

/* =========================================================
   ACCESS CONTROL DETAILS
   ========================================================= */

export type IncidentAccessControlDetails = {
  accessPointType?: IncidentAccessPointType;

  accessPointOther?: string;

  accessMethod?: IncidentAccessMethod;

  accessMethodOther?: string;

  authorized?: boolean;

  credentialDescription?: string;

  attemptedEntry?: boolean;

  additionalNotes?: string;
};

/* =========================================================
   PROPERTY DAMAGE DETAILS
   ========================================================= */

export type IncidentPropertyDamageDetails = {
  propertyCategory?: IncidentPropertyCategory;

  propertyCategoryOther?: string;

  propertyDescription?: string;

  suspectedCause?: IncidentDamageCause;

  suspectedCauseOther?: string;

  estimatedDamageAmount?: number;

  additionalNotes?: string;
};

/* =========================================================
   THEFT / LOSS DETAILS
   ========================================================= */

export type IncidentTheftLossDetails = {
  lossType?: IncidentLossType;

  propertyCategory?: IncidentPropertyLossCategory;

  propertyCategoryOther?: string;

  itemDescription?: string;

  estimatedValue?: number;

  lastKnownLocation?: string;

  /*
   * API representation = ISO string.
   *
   * Backend converts this to Date.
   */
  lastKnownAt?: string;

  lawEnforcementContacted?: boolean;

  agency?: string;

  caseNumber?: string;

  additionalNotes?: string;
};

/* =========================================================
   CLIENT DISSATISFACTION DETAILS
   ========================================================= */

export type IncidentClientDissatisfactionDetails = {
  complaintSource?: IncidentClientSource;

  complaintSourceOther?: string;

  complaintCategory?: IncidentComplaintCategory;

  complaintCategoryOther?: string;

  complaintSummary?: string;

  employeeOrTeamInvolved?: string;

  immediateResolution?: string;

  followUpRequired?: boolean;

  additionalNotes?: string;
};

/* =========================================================
   CLIENT PRAISE DETAILS
   ========================================================= */

export type IncidentClientPraiseDetails = {
  praiseSource?: IncidentClientSource;

  praiseSourceOther?: string;

  praisedEmployeeUserId?: string;

  praisedEmployeeNameSnapshot?: string;

  praiseCategory?: IncidentPraiseCategory;

  praiseCategoryOther?: string;

  praiseSummary?: string;

  additionalNotes?: string;
};

/* =========================================================
   INCIDENT REPORT
   ========================================================= */

export type IncidentReport = {
  _id: string;

  /*
   * SERVER-AUTHORITATIVE TENANT OWNERSHIP.
   *
   * These values come FROM the backend.
   *
   * They are not accepted as report ownership inputs
   * from the browser.
   */

  associatedCompany: string;

  associatedSite: string;

  reportType: IncidentReportType;

  status: IncidentReportStatus;

  title?: string;

  occurredAt?: string;

  locationLabel?: string;

  severity?: IncidentReportSeverity;

  personsInvolved: IncidentPersonInvolved[];

  narrative?: string;

  actionsTaken?: string;

  createdByUserId?: string;

  createdByNameSnapshot?: string;

  lastEditedByUserId?: string;

  submittedByUserId?: string;

  submittedAt?: string;

  notifySupervisor: boolean;

  attachments: IncidentAttachment[];

  sourceRedZoneObservationIds: string[];

  medical?: IncidentMedicalDetails;

  securityIncident?: IncidentSecurityDetails;

  suspiciousPerson?: IncidentSuspiciousPersonDetails;

  suspiciousVehicle?: IncidentSuspiciousVehicleDetails;

  fireSmoke?: IncidentFireSmokeDetails;

  safetyHazard?: IncidentSafetyHazardDetails;

  accessControl?: IncidentAccessControlDetails;

  propertyDamage?: IncidentPropertyDamageDetails;

  theftLoss?: IncidentTheftLossDetails;

  clientDissatisfaction?: IncidentClientDissatisfactionDetails;

  clientPraise?: IncidentClientPraiseDetails;

  createdAt: string;

  updatedAt: string;
};

/* =========================================================
   CREATE PAYLOAD
   ========================================================= */

export type CreateIncidentReportPayload = {
  /*
   * This is the only tenant/site ownership input.
   *
   * Backend:
   *
   * siteId
   *   -> authorized Site
   *   -> associatedCompany
   *   -> associatedSite
   */

  siteId: string;

  reportType: IncidentReportType;

  title?: string;

  occurredAt?: string;

  locationLabel?: string;

  severity?: IncidentReportSeverity;

  personsInvolved?: IncidentPersonInvolved[];

  narrative?: string;

  actionsTaken?: string;

  notifySupervisor?: boolean;

  medical?: IncidentMedicalDetails;

  securityIncident?: IncidentSecurityDetails;

  suspiciousPerson?: IncidentSuspiciousPersonDetails;

  suspiciousVehicle?: IncidentSuspiciousVehicleDetails;

  fireSmoke?: IncidentFireSmokeDetails;

  safetyHazard?: IncidentSafetyHazardDetails;

  accessControl?: IncidentAccessControlDetails;

  propertyDamage?: IncidentPropertyDamageDetails;

  theftLoss?: IncidentTheftLossDetails;

  clientDissatisfaction?: IncidentClientDissatisfactionDetails;

  clientPraise?: IncidentClientPraiseDetails;
};

/* =========================================================
   UPDATE PAYLOAD
   ========================================================= */

export type UpdateIncidentReportPayload = {
  reportType?: IncidentReportType;

  title?: string;

  occurredAt?: string;

  locationLabel?: string;

  severity?: IncidentReportSeverity;

  personsInvolved?: IncidentPersonInvolved[];

  narrative?: string;

  actionsTaken?: string;

  notifySupervisor?: boolean;

  medical?: IncidentMedicalDetails;

  securityIncident?: IncidentSecurityDetails;

  suspiciousPerson?: IncidentSuspiciousPersonDetails;

  suspiciousVehicle?: IncidentSuspiciousVehicleDetails;

  fireSmoke?: IncidentFireSmokeDetails;

  safetyHazard?: IncidentSafetyHazardDetails;

  accessControl?: IncidentAccessControlDetails;

  propertyDamage?: IncidentPropertyDamageDetails;

  theftLoss?: IncidentTheftLossDetails;

  clientDissatisfaction?: IncidentClientDissatisfactionDetails;

  clientPraise?: IncidentClientPraiseDetails;
};

/* =========================================================
   HISTORY FILTERS
   ========================================================= */

export type GetIncidentReportsFilters = {
  siteId: string;

  from?: string;

  to?: string;

  reportType?: IncidentReportType;

  severity?: IncidentReportSeverity;

  status?: IncidentReportStatus;

  createdByUserId?: string;

  limit?: number;

  skip?: number;
};

/* =========================================================
   HISTORY RESULT
   ========================================================= */

export type IncidentReportHistoryResult = {
  reports: IncidentReport[];

  total: number;

  limit: number;

  skip: number;
};

/* =========================================================
   API RESPONSE
   ========================================================= */

export type ApiResponse<T = any> = {
  success: boolean;

  data?: T;

  msg?: string;
};

/* =========================================================
   AUTH HELPERS
   ========================================================= */

async function getIdToken(): Promise<string> {
  const { tokens } = await fetchAuthSession();

  const idToken = tokens?.idToken?.toString();

  if (!idToken) {
    throw new Error('NO SESSION');
  }

  return idToken;
}

function authHeaders(idToken: string) {
  return {
    Authorization: `Bearer ${idToken}`,
  };
}

/* =========================================================
   CREATE INCIDENT REPORT
   ========================================================= */

export async function createIncidentReport(
  payload: CreateIncidentReportPayload
): Promise<ApiResponse<IncidentReport>> {
  const idToken = await getIdToken();

  const res = await api.post(
    '/incident-reports',
    {
      ...payload,

      token: idToken,
    },
    {
      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: raw?.data?.report,
  };
}

/* =========================================================
   GET INCIDENT REPORT HISTORY
   ========================================================= */

export async function getIncidentReports(
  filters: GetIncidentReportsFilters
): Promise<ApiResponse<IncidentReportHistoryResult>> {
  const idToken = await getIdToken();

  const params: Record<string, string | number> = {
    siteId: filters.siteId,
  };

  if (filters.from) {
    params.from = filters.from;
  }

  if (filters.to) {
    params.to = filters.to;
  }

  if (filters.reportType) {
    params.reportType = filters.reportType;
  }

  if (filters.severity) {
    params.severity = filters.severity;
  }

  if (filters.status) {
    params.status = filters.status;
  }

  if (filters.createdByUserId) {
    params.createdByUserId = filters.createdByUserId;
  }

  if (filters.limit !== undefined) {
    params.limit = filters.limit;
  }

  if (filters.skip !== undefined) {
    params.skip = filters.skip;
  }

  const res = await api.get('/incident-reports', {
    params,

    headers: authHeaders(idToken),
  });

  const raw = res.data;

  const result = raw?.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: {
      reports: Array.isArray(result?.reports) ? result.reports : [],

      total: Number(result?.total ?? 0),

      limit: Number(result?.limit ?? filters.limit ?? 100),

      skip: Number(result?.skip ?? filters.skip ?? 0),
    },
  };
}

/* =========================================================
   GET ONE INCIDENT REPORT
   ========================================================= */

export async function getIncidentReportById(
  reportId: string,
  siteId: string
): Promise<ApiResponse<IncidentReport>> {
  const idToken = await getIdToken();

  const res = await api.get(
    `/incident-reports/${encodeURIComponent(reportId)}`,
    {
      params: {
        siteId,
      },

      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: raw?.data?.report,
  };
}

/* =========================================================
   UPDATE INCIDENT REPORT DRAFT
   ========================================================= */

export async function updateIncidentReportDraft(
  reportId: string,
  siteId: string,
  payload: UpdateIncidentReportPayload
): Promise<ApiResponse<IncidentReport>> {
  const idToken = await getIdToken();

  const res = await api.patch(
    `/incident-reports/${encodeURIComponent(reportId)}`,
    {
      siteId,

      ...payload,

      token: idToken,
    },
    {
      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: raw?.data?.report,
  };
}

/* =========================================================
   SUBMIT INCIDENT REPORT
   ========================================================= */

export async function submitIncidentReport(
  reportId: string,
  siteId: string
): Promise<ApiResponse<IncidentReport>> {
  const idToken = await getIdToken();

  const res = await api.post(
    `/incident-reports/${encodeURIComponent(reportId)}/submit`,
    {
      siteId,

      token: idToken,
    },
    {
      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: raw?.data?.report,
  };
}

/* =========================================================
   UPLOAD INCIDENT REPORT ATTACHMENTS
   ========================================================= */

export async function uploadIncidentReportAttachments(
  reportId: string,
  siteId: string,
  files: File[]
): Promise<ApiResponse<IncidentReport>> {
  if (!reportId) {
    throw new Error('reportId is required.');
  }

  if (!siteId) {
    throw new Error('siteId is required.');
  }

  if (!Array.isArray(files) || files.length === 0) {
    throw new Error('At least one attachment is required.');
  }

  const idToken = await getIdToken();

  const formData = new FormData();

  formData.append('siteId', siteId);

  files.forEach((file) => {
    formData.append('attachments', file, file.name);
  });

  /*
   * IMPORTANT:
   *
   * Do NOT manually set Content-Type here.
   *
   * The browser/Axios must generate the multipart boundary
   * automatically for FormData.
   */
  const res = await api.post(
    `/incident-reports/${encodeURIComponent(reportId)}/attachments`,
    formData,
    {
      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: raw?.data?.report,
  };
}

/* =========================================================
   DELETE INCIDENT REPORT ATTACHMENT
   ========================================================= */

export async function deleteIncidentReportAttachment(
  reportId: string,
  siteId: string,
  attachmentId: string
): Promise<ApiResponse<IncidentReport>> {
  if (!reportId) {
    throw new Error('reportId is required.');
  }

  if (!siteId) {
    throw new Error('siteId is required.');
  }

  if (!attachmentId) {
    throw new Error('attachmentId is required.');
  }

  const idToken = await getIdToken();

  const res = await api.delete(
    `/incident-reports/${encodeURIComponent(
      reportId
    )}/attachments/${encodeURIComponent(attachmentId)}`,
    {
      params: {
        siteId,
      },

      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: raw?.data?.report,
  };
}

/* =========================================================
   HEIGHT CONVERSION HELPERS
   =========================================================
 *
 * Canonical database/API unit = centimeters.
 *
 * Web/native may display imperial units.
 * ========================================================= */

/**
 * Convert feet + inches entered by the protector into
 * canonical centimeters before sending to the API.
 *
 * Examples:
 *
 * 5'11" -> 180 cm
 * 6'0"  -> 183 cm
 */
export function feetInchesToCm(feet: number, inches: number): number {
  const safeFeet = Number.isFinite(feet) ? feet : 0;

  const safeInches = Number.isFinite(inches) ? inches : 0;

  const totalInches = safeFeet * 12 + safeInches;

  return Math.round(totalInches * 2.54);
}

/**
 * Convert canonical centimeters from the API into
 * feet/inches for display.
 */
export function cmToFeetInches(centimeters: number | undefined): {
  feet: number;
  inches: number;
} | null {
  if (centimeters === undefined || !Number.isFinite(centimeters)) {
    return null;
  }

  const totalInches = Math.round(centimeters / 2.54);

  return {
    feet: Math.floor(totalInches / 12),

    inches: totalInches % 12,
  };
}

/* =========================================================
   INCIDENT REPORT CONFIG
   ========================================================= */

export type IncidentReportConfig = {
  reportTypes: readonly IncidentReportType[];

  statuses: readonly IncidentReportStatus[];

  severities: readonly IncidentReportSeverity[];

  personRoles: readonly IncidentPersonRole[];

  directions: readonly IncidentDirection[];

  usStates: readonly IncidentUSState[];

  suspiciousPerson: {
    sexPresentations: readonly IncidentPersonSexPresentation[];

    ageRanges: readonly IncidentPersonAgeRange[];

    builds: readonly IncidentPersonBuild[];

    hairColors: readonly IncidentHairColor[];

    hairLengths: readonly IncidentHairLength[];

    facialHairTypes: readonly IncidentFacialHairType[];
  };

  suspiciousVehicle: {
    bodyTypes: readonly IncidentVehicleBodyType[];

    colors: readonly IncidentVehicleColor[];
  };

  medical: {
    incidentNatures: readonly IncidentMedicalNature[];

    careActions: readonly IncidentMedicalCareAction[];
  };

  securityIncident: {
    subtypes: readonly IncidentSecuritySubtype[];
  };

  fireSmoke: {
    sources: readonly IncidentFireSource[];
  };

  safetyHazard: {
    hazardTypes: readonly IncidentHazardType[];
  };

  accessControl: {
    methods: readonly IncidentAccessMethod[];

    pointTypes: readonly IncidentAccessPointType[];
  };

  propertyDamage: {
    categories: readonly IncidentPropertyCategory[];

    causes: readonly IncidentDamageCause[];
  };

  theftLoss: {
    lossTypes: readonly IncidentLossType[];

    propertyCategories: readonly IncidentPropertyLossCategory[];
  };

  clientFeedback: {
    sources: readonly IncidentClientSource[];

    complaintCategories: readonly IncidentComplaintCategory[];

    praiseCategories: readonly IncidentPraiseCategory[];
  };
};

/* =========================================================
   GET INCIDENT REPORT CONFIG
   ========================================================= */

export async function getIncidentReportConfig(): Promise<
  ApiResponse<IncidentReportConfig>
> {
  const idToken = await getIdToken();

  const res = await api.get('/incident-reports/config', {
    headers: authHeaders(idToken),
  });

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: raw?.data?.config,
  };
}

/* =========================================================
   DELETE INCIDENT REPORT DRAFT
   ========================================================= */

export async function deleteIncidentReportDraft(
  reportId: string,
  siteId: string
): Promise<
  ApiResponse<{
    reportId: string;
  }>
> {
  const idToken = await getIdToken();

  const res = await api.delete(
    `/incident-reports/${encodeURIComponent(reportId)}`,
    {
      params: {
        siteId,
      },

      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: raw?.data,
  };
}
